import React from 'react';
import { Box, Typography, Card, CardContent, Grid, IconButton, Tooltip, Alert } from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { addNotification } from '../../../store/uiSlice';
import { WritingStyle, IllustrationStyle } from './types';

interface PromptExpandSectionProps {
  style: WritingStyle | IllustrationStyle;
  type: 'writing' | 'illustration';
}

const PromptExpandSection: React.FC<PromptExpandSectionProps> = ({ style, type }) => {
  const dispatch = useDispatch<AppDispatch>();

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      dispatch(addNotification({ type: 'success', message: `${label} copied to clipboard` }));
    }).catch(() => {
      dispatch(addNotification({ type: 'error', message: 'Failed to copy to clipboard' }));
    });
  };

  const hasStructuredData = style.styleDNA ||
    (style as WritingStyle).transformationFormula ||
    (style as IllustrationStyle).visualApproach ||
    (style as IllustrationStyle).promptConstructionFormula ||
    style.truthIntegration;

  if (!hasStructuredData && !style.prompt) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No prompt data available for this style.
      </Alert>
    );
  }

  const sectionStyle = {
    bgcolor: '#f8fafc',
    borderRadius: 2,
    border: '1px solid #e2e8f0',
  };

  const renderSection = (title: string, emoji: string, content: string, copyLabel: string) => (
    <Grid item xs={12}>
      <Card variant="outlined" sx={sectionStyle}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
              {emoji} {title}
            </Typography>
            <Tooltip title={`Copy ${copyLabel}`}>
              <IconButton size="small" onClick={() => handleCopy(content, copyLabel)}>
                <CopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '0.8rem', color: '#475569' }}>
            {content}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {content.length} characters
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#1A5F7A', fontSize: '0.78rem' }}>
        GROK AI PROMPT ARCHITECTURE
      </Typography>
      <Grid container spacing={1.5}>
        {style.styleDNA && renderSection('Style DNA', '', style.styleDNA, 'Style DNA')}
        {type === 'writing' && (style as WritingStyle).transformationFormula &&
          renderSection('Transformation Formula', '', (style as WritingStyle).transformationFormula!, 'Transformation Formula')}
        {type === 'illustration' && (style as IllustrationStyle).visualApproach &&
          renderSection('Visual Approach', '', (style as IllustrationStyle).visualApproach!, 'Visual Approach')}
        {type === 'illustration' && (style as IllustrationStyle).promptConstructionFormula &&
          renderSection('Prompt Construction Formula', '', (style as IllustrationStyle).promptConstructionFormula!, 'Prompt Construction Formula')}
        {style.truthIntegration && renderSection('Truth Integration', '', style.truthIntegration, 'Truth Integration')}
        {!hasStructuredData && style.prompt && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ ...sectionStyle, bgcolor: '#fffbeb', borderColor: '#fde68a' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                    Legacy Prompt (Not Structured)
                  </Typography>
                  <Tooltip title="Copy Legacy Prompt">
                    <IconButton size="small" onClick={() => handleCopy(style.prompt, 'Legacy Prompt')}>
                      <CopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                  {style.prompt}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {style.prompt.length} characters
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PromptExpandSection;
